import { rem } from 'polished';
import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import {
  StyledSizeProps,
  StyledSpacingProps,
  sizeStyles,
  spacingStyles,
} from '../../styles';
import { borderRadius } from '../../theme';
import colors from '../../theme/constants/colors';
import { ValueOf } from '../../types';
import BaseSpinner from '../Spinner/Spinner';

import { DataTableClassNames, DataTableVariableNames } from './constants';

const tableVariableStyles = css(
  (): Record<ValueOf<typeof DataTableVariableNames>, string | undefined> => ({
    [DataTableVariableNames.BackgroundColor]: colors.white,
    [DataTableVariableNames.BodyBorderColor]: colors.sand,
    [DataTableVariableNames.HeaderBorderColor]: '#c2c4ce',
    [DataTableVariableNames.HeaderBackgroundColor]: '#f4f4f6',
    [DataTableVariableNames.HeaderTextColor]: '#84889c',
    [DataTableVariableNames.BorderRadius]: borderRadius['3x'],
    [DataTableVariableNames.BorderWidth]: rem(1),
    [DataTableVariableNames.OutlineWidth]: rem(2),
    [DataTableVariableNames.OutlineColor]: '#8497fa',
    [DataTableVariableNames.ShadowWidth]: rem(4),
    [DataTableVariableNames.ShadowColor]: '#dbe1ff',
    [DataTableVariableNames.FixedColumnWidth]: 'auto',
    [DataTableVariableNames.FixedActionColumnWidth]: rem(60),
    [DataTableVariableNames.FilterColor]: '#8497FA',
  }),
);

/** @see https://medium.com/@denno020/html-table-with-rounded-borders-f38999e14c89 */
const roundedTableBorderRadiusStyles = css`
  border-spacing: 0;
  border-collapse: separate; /* Very important, having this set to collapse will break border-radius */
  border-radius: var(${DataTableVariableNames.BorderRadius});

  & > thead,
  & > tbody {
    & > tr {
      & > th {
        border: var(${DataTableVariableNames.BorderWidth}) solid
          var(${DataTableVariableNames.HeaderBorderColor});
      }

      & > td {
        border: var(${DataTableVariableNames.BorderWidth}) solid
          var(${DataTableVariableNames.BodyBorderColor});
      }

      /* The left borders of table cells will be used as the borders between columns */
      & > td + th,
      & > th + th,
      & > th + td,
      & > td + td,
      & > td:first-child,
      & > th:first-child {
        border-right-width: 0;
      }

      & > td:last-child,
      & > th:last-child {
        border-right-width: 1px;
      }

      /** action cell is exception as it should have no border separator */
      & > td.${DataTableClassNames.Action}, th.${DataTableClassNames.Action} {
        border-left-width: 0;
        border-right-width: var(${DataTableVariableNames.BorderWidth});
      }
    }

    /* The bottom borders of table cells will be used as the borders between rows */
    /* stylelint-disable-next-line no-descending-specificity */
    & > tr + tr > th,
    & > tr + tr > td {
      border-top: 0;
    }
  }

  /** removing top border from first row in tbody as to not conflict with bottom-border from header row */
  & > tbody > tr:first-child > td {
    border-top: 0;
  }

  /** modifying the border radius of the first thead row */
  & > thead > tr:first-child th:first-child {
    border-top-left-radius: var(${DataTableVariableNames.BorderRadius});
  }

  & > thead > tr:first-child th:last-child {
    border-top-right-radius: var(${DataTableVariableNames.BorderRadius});
  }

  /** modifying the border radius of the last row */
  & > tbody > tr:last-child {
    border-bottom-left-radius: var(${DataTableVariableNames.BorderRadius});
    border-bottom-right-radius: var(${DataTableVariableNames.BorderRadius});

    td:first-child {
      border-bottom-left-radius: var(${DataTableVariableNames.BorderRadius});
    }

    td:last-child {
      border-bottom-right-radius: var(${DataTableVariableNames.BorderRadius});
    }
  }
`;

/**
 * User can specify basic `th` and we might have some empty `th` reserved for action,
 * so we want all th to have same default background color for consistency
 * */
const defaultTableHeadStyles = css`
  & > thead {
    border-radius: var(${DataTableVariableNames.BorderRadius});
    background-color: var(${DataTableVariableNames.HeaderBackgroundColor});
    color: var(${DataTableVariableNames.HeaderTextColor});
  }
`;

/** Conditionally hides/shows visually action cell based on hover/focus */
const actionRowVisibilityStyles = css`
  .${DataTableClassNames.ActionChildren} {
    opacity: 0;
  }

  tr:hover {
    .${DataTableClassNames.ActionChildren} {
      opacity: 1;
    }
  }

  td:has(:focus-visible) .${DataTableClassNames.ActionChildren} {
    opacity: 1;
  }
`;

const tableFixedLayoutStyles = css`
  table-layout: fixed;

  & > colgroup > col {
    width: var(${DataTableVariableNames.FixedColumnWidth});

    &.${DataTableClassNames.Action} {
      width: var(${DataTableVariableNames.FixedActionColumnWidth});
    }
  }
`;

const loadingStyles = css`
  cursor: progress;
  opacity: 0.5;
`;

interface TableProps {
  $layout: CSSProperties['tableLayout'];
  $isLoading: boolean;
}

type PaddingProps = Pick<
  StyledSpacingProps,
  '$p' | '$pt' | '$pr' | '$pb' | '$pl'
>;

export const Table = styled.table<TableProps & PaddingProps>`
  box-sizing: border-box;
  width: 100%;
  ${roundedTableBorderRadiusStyles};
  ${defaultTableHeadStyles}
  ${actionRowVisibilityStyles}
  ${({ $layout }) => ($layout === 'fixed' ? tableFixedLayoutStyles : '')}
  ${({ $isLoading }) => $isLoading && loadingStyles};

  & > tbody {
    background-color: var(${DataTableVariableNames.BackgroundColor});
  }

  /** 
  * For some reason DataTable behaves weird if we have padding in TableScrollArea and sticky action,
  * so  we use padding in Table (child of scrollarea) instead
  */
  padding: calc(
    var(${DataTableVariableNames.OutlineWidth}) +
      var(${DataTableVariableNames.ShadowWidth})
  );
  padding-top: 0;

  ${spacingStyles}
`;

/**
 * @note
 * Overflow styles generally only work with block elements or fixed width elements,
 * but not with inline or table elements.
 * For this reason we create container component over the table
 * */
export const TableScrollArea = styled.div<
  StyledSizeProps & Omit<StyledSpacingProps, keyof PaddingProps>
>`
  ${tableVariableStyles}

  z-index: 0;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  overflow: auto;
  white-space: nowrap;
  ${sizeStyles}
  ${spacingStyles}
`;

export const Spinner = styled(BaseSpinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%);
  ${({ theme }) => `
    width: ${theme.core.iconSize.l};
    height: ${theme.core.iconSize.l};
  `}
`;
